import type { UseMutationResult } from '@tanstack/react-query';
import {
  useQueryClient,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
  useMutation,
} from '@tanstack/react-query';
import { createContext, useCallback } from 'react';
import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type {
  GetAllPaymentInfoResponse,
  GetReceiptBatchPaymentPhoenixResult,
  IPaymentPhoenixApi,
  MangopayPaymentResolution,
  MangopayReceiptBatchEntry,
  MangopayReceiptBatchPayment,
  PayinRequest,
  PaymentInfo,
  PendingCharge,
  ResetFailedPendingChargesResponse,
} from './api';
import { MangopayPaymentStatus, PaymentPhoenixApi } from './api';
import type { PaymentInitiationDto } from '@pflegenavi/shared/api';
import { PendingChargeStatus } from '@pflegenavi/shared/api';

const ApiContext = createContext<IPaymentPhoenixApi | undefined>(undefined);
const { useApi: usePaymentPhoenixApi, ApiProvider: PaymentPhoenixApiProvider } =
  makeApiProvider({
    name: 'PaymentPhoenix',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) => {
      return new PaymentPhoenixApi(tenantId, auth, apiUrl);
    },
  });

export {
  usePaymentPhoenixApi,
  PaymentPhoenixApiProvider,
  ApiContext as PaymentPhoenixApiContext,
  GetReceiptBatchPaymentPhoenixResult,
  MangopayReceiptBatchEntry,
  MangopayPaymentResolution,
  MangopayPaymentStatus,
  MangopayReceiptBatchPayment,
  GetAllPaymentInfoResponse,
  PaymentInfo,
  PendingCharge,
  ResetFailedPendingChargesResponse,
};

export const getReceiptBatchPaymentPhoenix = (
  receiptBatchId: string | undefined
): QueryKey => ['GET_RECEIPT_BATCH_PAYMENT_PHOENIX', receiptBatchId];

export const useGetNursingHomeReceiptBatchPaymentPhoenix = ({
  receiptBatchId,
  options,
}: {
  receiptBatchId: string | undefined;
  options?: Omit<
    UseQueryOptions<GetReceiptBatchPaymentPhoenixResult, unknown>,
    'queryFn' | 'queryKey'
  >;
}): UseQueryResult<GetReceiptBatchPaymentPhoenixResult, unknown> => {
  return useApiQuery(
    usePaymentPhoenixApi,
    getReceiptBatchPaymentPhoenix(receiptBatchId),
    (api) => {
      if (!receiptBatchId) {
        throw new Error('receiptBatchId is required to get payment');
      }
      return api.getReceiptBatchPaymentPhoenix({
        params: {
          receiptBatchId,
        },
      });
    },
    {
      ...options,
      enabled: options?.enabled ?? !!receiptBatchId,
    }
  );
};

export const PAYMENTS_INFO_KEY = (residentId: string | undefined): QueryKey => {
  if (!residentId) {
    return ['payments-info'];
  }
  return ['payments-info', residentId];
};

export const GET_ALL_PAYMENT_INFO_PHOENIX_KEY = (
  nursingHomeId: string | undefined
): QueryKey => {
  return ['GET_ALL_PAYMENT_INFO_PHOENIX', nursingHomeId];
};

export const useGetAllPaymentInfoPhoenix = (
  nursingHomeId?: string,
  options?: Omit<
    UseQueryOptions<GetAllPaymentInfoResponse, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetAllPaymentInfoResponse, unknown> => {
  return useApiQuery(
    usePaymentPhoenixApi,
    GET_ALL_PAYMENT_INFO_PHOENIX_KEY(nursingHomeId),
    (api) => {
      if (!nursingHomeId) {
        throw new Error('nursingHomeId is required');
      }
      return api.getAllPaymentInfo({
        params: {
          nursingHomeId,
        },
      });
    },
    options
  );
};

export const useGetPaymentInfoPhoenix = (
  residentId: string,
  options?: Omit<
    UseQueryOptions<PaymentInfo, unknown, PaymentInfo>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<PaymentInfo, unknown> => {
  const select = useCallback((data: PaymentInfo): PaymentInfo => {
    return {
      ...data,
      additional_data: {
        ...data.additional_data,
        nursing_home_payments_disabled_until: data.additional_data
          .nursing_home_payments_disabled_until
          ? new Date(data.additional_data.nursing_home_payments_disabled_until)
          : undefined,
      },
      pending_charges: data.pending_charges
        .filter(
          (pendingCharge) =>
            pendingCharge.pending_charge_status !== PendingChargeStatus.success
        )
        .map((pendingCharge) => ({
          ...pendingCharge,
          charge_date: new Date(pendingCharge.charge_date),
          inserted_at: new Date(pendingCharge.inserted_at),
        }))
        .sort((a, b) => {
          const dateDiff = b.charge_date.getTime() - a.charge_date.getTime();
          if (dateDiff !== 0) {
            return dateDiff;
          }
          // If charge_date is equal, sort by inserted_at (most recent first)
          return b.inserted_at.getTime() - a.inserted_at.getTime();
        }),
    };
  }, []);

  return useApiQuery(
    usePaymentPhoenixApi,
    PAYMENTS_INFO_KEY(residentId),
    (api) => {
      return api?.getPaymentInfo({
        params: {
          residentId,
        },
      });
    },
    {
      ...options,
      select,
      enabled: residentId !== '',
    }
  );
};

export const useGetPaymentInfoForFamilyMemberPhoenix = (
  residentId: string,
  tenant: string,
  options?: Omit<
    UseQueryOptions<PaymentInfo, unknown, PaymentInfo>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<PaymentInfo, unknown> => {
  const select = useCallback((data: PaymentInfo): PaymentInfo => {
    return {
      ...data,
      additional_data: {
        ...data.additional_data,
        nursing_home_payments_disabled_until: data.additional_data
          .nursing_home_payments_disabled_until
          ? new Date(data.additional_data.nursing_home_payments_disabled_until)
          : undefined,
      },
      pending_charges: (
        data.pending_charges
          ?.filter(
            (charge) =>
              charge.pending_charge_status !== PendingChargeStatus.success
          )
          .map((charge) => ({
            ...charge,
            charge_date: new Date(charge.charge_date),
            inserted_at: new Date(charge.inserted_at),
          })) || []
      ).sort((a, b) => {
        const dateDiff = b.charge_date.getTime() - a.charge_date.getTime();
        if (dateDiff !== 0) {
          return dateDiff;
        }
        // If charge_date is equal, sort by inserted_at (most recent first)
        return b.inserted_at.getTime() - a.inserted_at.getTime();
      }),
    };
  }, []);

  return useApiQuery(
    usePaymentPhoenixApi,
    PAYMENTS_INFO_KEY(residentId),
    (api) => {
      return api?.getPaymentInfoForFamilyMember({
        params: {
          residentId,
          tenant,
        },
      });
    },
    {
      ...options,
      select,
      enabled: residentId !== '',
    }
  );
};

export const useCreatePayinPhoenix = (): UseMutationResult<
  void,
  unknown,
  PayinRequest
> => {
  const api = usePaymentPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['CREATE_PAYIN'],
    mutationFn: (params) => {
      return api.createPayin(params);
    },
    onSuccess: async (_, params) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: PAYMENTS_INFO_KEY(params.params.resident_id),
        }),
      ]);
    },
  });
};

export const useResetFailedPendingChargesPhoenix = (
  residentId: string
): UseMutationResult<
  ResetFailedPendingChargesResponse,
  unknown,
  { notes: string }
> => {
  const api = usePaymentPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['RESET_FAILED_PENDING_CHARGES', residentId],
    mutationFn: ({ notes }) => {
      return api.resetFailedPendingCharges({
        params: { residentId },
        body: { notes },
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: PAYMENTS_INFO_KEY(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: GET_ALL_PAYMENT_INFO_PHOENIX_KEY(undefined),
        }),
      ]);
    },
  });
};

export const useSetPaymentInitiationPhoenix = (
  residentId: string,
  familyMemberId: string
): UseMutationResult<{ success: boolean }, unknown, PaymentInitiationDto> => {
  const api = usePaymentPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['SET_PAYMENT_INITIATION_PHOENIX', residentId, familyMemberId],
    mutationFn: (data) => {
      return api.setPaymentInitiation({
        params: { residentId, familyMemberId },
        body: data,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: PAYMENTS_INFO_KEY(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: GET_ALL_PAYMENT_INFO_PHOENIX_KEY(undefined),
        }),
      ]);
    },
  });
};
